import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { AnalyticsEventNames } from 'constants/analytics';
import useAuth from 'hooks/useAuth';
import useGlobalStore from 'hooks/useGlobalStore';
import useNanoBannerAnalyticsProperties from 'hooks/useNanoBannerAnalyticsProperties';
import { trackPage } from 'utils/analyticsUtils';

/**
 * Track page views. Prefer AnalyticsEventNames.PAGE_VIEW and pass page identity via
 * optionalPageViewProperties['page name'] using AnalyticsPageNames.
 * Includes best-effort `promo_banner` and `referral_banner` (copy when showing).
 * Banners can load after page view, so these may be null until a later page view.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Optionally disable tracking until a condition is met. Useful for including event properties that are loaded async.
 * @param {string} [options.eventName=AnalyticsEventNames.PAGE_VIEW] - must be a value from AnalyticsEventNames
 * @param {boolean} [options.includeUtmParams=false] - Optionally include utm params as event properties. Note: this will also include the promo param.
 * @param {Object} [options.optionalPageViewProperties={}] - Optionally include custom event properties (including `page name`).
 */
export default function useTrackPageView({
    enabled = true,
    eventName = AnalyticsEventNames.PAGE_VIEW,
    includeUtmParams = false,
    optionalPageViewProperties = {},
} = {}) {
    const trackedRef = useRef(false);
    const { pathname, search } = useLocation();
    const { isMisfitsTheme } = useGlobalStore();
    const { authStatus } = useAuth();
    const isLoggedIn = authStatus === 'authenticated';
    const nanoBannerProperties = useNanoBannerAnalyticsProperties();

    useEffect(() => {
        // Wait for auth so is_logged_in is accurate; trackedRef would otherwise lock in a false value.
        if (authStatus === 'configuring' || trackedRef.current === pathname || !enabled) return;

        trackPage({
            eventName,
            extraProperties: {
                ...optionalPageViewProperties,
                ...nanoBannerProperties,
            },
            includeUtmParams,
            isLoggedIn,
            isMisfitsTheme,
            pathname,
            search,
        });

        trackedRef.current = pathname;
    }, [
        authStatus,
        enabled,
        eventName,
        includeUtmParams,
        isLoggedIn,
        isMisfitsTheme,
        nanoBannerProperties,
        optionalPageViewProperties,
        pathname,
        search,
    ]);
}
